import React, { useState, useEffect, useContext, useRef } from 'react';
import CloseIcon from "@mui/icons-material/Close";
import {
    Typography, FormControlLabel, Switch, CircularProgress,
} from '@mui/material';

import { InputSelector } from "../../../components/UI/InputSelector"; 
import { DropdownSelector } from "../../../components/UI/DropdownSelector"; 

import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import styles from "../../../styles/pages/DepositMethods/components/DepositMethodModal.module.css"
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";

const translations = {
    ru: {
        title: "Добавить Метод Депозита",
        confirm: "Сохранить",
        delete: "Удалить метод",
        cancel: "Отмена",
        editTitle: "Редактировать Метод Депозита",
        keyDisabledHelper: "Ключ нельзя изменить в режиме редактирования.",
        typeDisabledHelper: "Тип нельзя изменить в режиме редактирования.",
        isHidden: "Скрыт (Is Hidden)",
        visible: "Виден",
        hidden: "Скрыт",
        icon: "Иконка",
        currentIcon: "Текущая иконка",
    },
    en: {
        title: "Add Deposit Method",
        confirm: "Save",
        delete: "Delete Method",
        cancel: "Cancel",
        editTitle: "Edit Deposit Method",
        keyDisabledHelper: "Key cannot be changed in edit mode.",
        typeDisabledHelper: "Type cannot be changed in edit mode.",
        isHidden: "Is Hidden",
        visible: "Visible",
        hidden: "Hidden",
        icon: "Icon",
        currentIcon: "Current Icon",
    },
};

const ALLOWED_TYPES = [
    { value: 'crypto', label: 'Криптовалюта' },
    { value: 'bank', label: 'Банк' },
    { value: 'card_gateway', label: 'Card Gateway' },
];

export const DepositMethodModal = ({
    open,
    handleClose,
    handleSave,
    handleDelete,
    isSubmitting,
    isDeleting,
    initialData = null
}) => {
    
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;
    
    const isEditing = !!initialData;
    const modalRef = useRef(null);

    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    const [formData, setFormData] = useState({
        key: initialData?.key || '',
        type: initialData?.type || 'crypto',
        label: initialData?.label || '',
        address: initialData?.address || '',
        is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
        is_hidden: initialData?.is_hidden !== undefined ? initialData.is_hidden : false,
        icon: null,
        icon_preview: initialData?.icon || '',
        sort: initialData?.sort || 0,
    });
    const isCardGateway = formData.type === 'card_gateway';
    
    const [focused, setFocused] = useState({});
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;

        setFormData({
            key: initialData?.key || '',
            type: initialData?.type || 'crypto',
            label: initialData?.label || '',
            address: initialData?.address || '',
            is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
            is_hidden: initialData?.is_hidden !== undefined ? initialData.is_hidden : false,
            icon: null,
            icon_preview: initialData?.icon || '',
            sort: initialData?.sort || 0,
        });
        setErrors({});
        setFocused({});
        setTouched({});

        const contentHtml = initialData?.content_html || '';
        if (contentHtml) {
            const contentBlock = htmlToDraft(contentHtml);
            if (contentBlock) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
        } else {
            setEditorState(EditorState.createEmpty());
        }
    }, [initialData, open]);
    
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleClose();
            }
        };
        
        if (open) {
             document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, handleClose]);

    const handleChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleFocus = (key) => {
        setFocused((prev) => ({ ...prev, [key]: true }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };
    
    const handleBlur = (key) => {
        setFocused((prev) => ({ ...prev, [key]: false }));
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handleSwitchChange = () => {
        setFormData(prev => ({
            ...prev,
            is_active: !prev.is_active,
        }));
    };

    const handleHiddenSwitchChange = () => {
        setFormData(prev => ({
            ...prev,
            is_hidden: !prev.is_hidden,
        }));
    };

    const handleIconChange = (event) => {
        const file = event.target.files?.[0] || null;

        setFormData((prev) => ({
            ...prev,
            icon: file,
            icon_preview: file ? URL.createObjectURL(file) : prev.icon_preview,
        }));
    };

    const onEditorStateChange = (newEditorState) => {
        setEditorState(newEditorState);
        setErrors(prev => ({ ...prev, content_html: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.key.trim()) newErrors.key = 'Ключ обязателен.';
        if (!formData.label.trim()) newErrors.label = 'Название (Label) обязательно.';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const content_html = isCardGateway ? '' : draftToHtml(convertToRaw(editorState.getCurrentContent()));
            
            const finalData = { 
                ...formData, 
                address: isCardGateway ? '' : formData.address,
                content_html: content_html,
                sort: Number(formData.sort),
                is_hidden: Boolean(formData.is_hidden),
                icon: formData.icon,
            };
            
            handleSave(finalData, initialData?.id);
        }
    };

    const title = isEditing ? t.editTitle : t.title;

    if (!open) return null;

    const renderInputField = (key, label, type = "text", helperText = null) => (
        <div key={key} className={styles.inputRow}>
            <label className={`${isDark ? styles.darkLabel : ''}`}>{label}:</label>
            <InputSelector
                filterName={key}
                selectedValue={formData[key]}
                handleFilterChange={handleChange}
                onFocus={() => handleFocus(key)}
                onBlur={() => handleBlur(key)}
                hasError={!!errors[key]}
                errorMessage={errors[key]}
                isFocused={focused[key]}
                inputType={type}
                helperText={helperText}
                disabled={isEditing && key === 'key'}
                isDark={isDark}
            />
        </div>
    );

    return (
        <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
            <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                    <p className={`${isDark ? styles.darkModalLabelText : ''}`}>{title}</p>
                    <div onClick={handleClose} className={`${isDark ? styles.darkModalLabelCloseButton : ''}`}><CloseIcon /></div>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalContent}>
                    
                    <div className={styles.section}>
                        
                        {renderInputField('key', 'Ключ (Key)', 'text', isEditing ? t.keyDisabledHelper : null)}
                        
                        {renderInputField('label', 'Название (Label)', 'text')}
                        
                        <div className={styles.inputRow}>
                            <label className={`${isDark ? styles.darkLabel : ''}`}>Тип(Type):</label>
                            <DropdownSelector
                                filterName="type"
                                selectedValue={formData.type}
                                handleFilterChange={handleChange}
                                onFocus={() => handleFocus('type')}
                                onBlur={() => handleBlur('type')}
                                hasError={!!errors.type}
                                errorMessage={errors.type}
                                isFocused={focused.type}
                                options={ALLOWED_TYPES}
                                disabled={isEditing} 
                                helperText={isEditing ? t.typeDisabledHelper : null}
                                isDark={isDark}
                            />
                        </div>

                        {!isCardGateway && renderInputField('address', 'Адрес (Address)', 'text', 'Опционально. Используется для крипто-адресов.')}

                        {renderInputField('sort', 'Сортировка (Sort)', 'number')}

                        <div className={styles.inputRow}>
                            <label className={`${isDark ? styles.darkLabel : ''}`}>{t.icon}:</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleIconChange}
                                className={`${styles.fileInput} ${isDark ? styles.darkFileInput : ''}`}
                            />
                            {formData.icon_preview && (
                                <div className={styles.iconPreviewRow}>
                                    <span className={`${styles.iconPreviewLabel} ${isDark ? styles.darkLabel : ''}`}>{t.currentIcon}:</span>
                                    <img src={formData.icon_preview} alt={t.icon} className={styles.iconPreview} />
                                </div>
                            )}
                        </div>
                        
                        <div className={styles.inputRow}>
                            <label className={`${isDark ? styles.darkLabel : ''}`}>Активность(Is Active):</label>
                            <div 
                                className={`${styles.customSwitch} ${formData.is_active ? styles.isActive : ''} ${isDark ? styles.darkCustomSwitch : ''} ${formData.is_active && isDark ? styles.darkIsActive : ''}`} 
                                onClick={handleSwitchChange}
                            >
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleSwitchChange}
                                    style={{ display: 'none' }}
                                />
                                <span className={`${styles.switchLabel} ${isDark ? styles.darkSwitchLabel : ''} ${formData.is_active && isDark ? styles.darkIsActiveLabel : ''}`}>
                                    {formData.is_active ? "Активен" : "Неактивен"}
                                </span>
                            </div>
                        </div>

                        <div className={styles.inputRow}>
                            <label className={`${isDark ? styles.darkLabel : ''}`}>{t.isHidden}:</label>
                            <div
                                className={`${styles.customSwitch} ${!formData.is_hidden ? styles.isActive : styles.isInactiveSwitch} ${isDark ? styles.darkCustomSwitch : ''} ${!formData.is_hidden && isDark ? styles.darkIsActive : ''} ${formData.is_hidden && isDark ? styles.darkIsInactiveSwitch : ''}`}
                                onClick={handleHiddenSwitchChange}
                            >
                                <input
                                    type="checkbox"
                                    name="is_hidden"
                                    checked={formData.is_hidden}
                                    onChange={handleHiddenSwitchChange}
                                    style={{ display: 'none' }}
                                />
                                <span className={`${styles.switchLabel} ${formData.is_hidden ? styles.inactiveSwitchLabel : ''} ${isDark ? styles.darkSwitchLabel : ''} ${!formData.is_hidden && isDark ? styles.darkIsActiveLabel : ''} ${formData.is_hidden && isDark ? styles.darkIsInactiveLabel : ''}`}>
                                    {formData.is_hidden ? t.hidden : t.visible}
                                </span>
                            </div>
                        </div>
                        
                        {!isCardGateway && (
                            <div className={styles.editorFullWidth}>
                                <label className={`${isDark ? styles.darkLabel : ''}`}>Содержание (Content HTML):</label>
                                <div className={`${styles.editorContainer} ${isDark ? styles.darkEditorContainer : ''}`}>
                                    {open && (
                                        <Editor
                                            editorState={editorState}
                                            wrapperClassName={styles.editorWrapper}
                                            editorClassName={`${styles.editorContent} ${isDark ? styles.darkEditorContent : ''}`}
                                            onEditorStateChange={onEditorStateChange}
                                            toolbarClassName={`${isDark ? styles.darkToolbar : ''}`}
                                            toolbar={{
                                                options: ['history', 'inline', 'blockType', 'list', 'textAlign', 'link', 'colorPicker', 'image'],
                                                inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] }, 
                                                list: { inDropdown: false }, 
                                                textAlign: { inDropdown: false }, 
                                                link: { inDropdown: false }, 
                                                history: { inDropdown: false }, 
                                                colorPicker: { inDropdown: false }, 
                                                image: { 
                                                    urlEnabled: true,
                                                    uploadEnabled: false, 
                                                    alignmentEnabled: true,
                                                    defaultSize: { height: 'auto', width: '100%' },
                                                },
                                                blockType: { inDropdown: true },
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {errors.general && (
                            <div className={`${styles.textError} ${isDark ? styles.darkTextError : ''}`}>{errors.general}</div>
                        )}
                    </div>
                    
                    <div className={`${styles.modalFooter} ${isDark ? styles.darkModalFooter : ''}`}>
                        <div className={`${styles.resetWrapper} ${isDark ? styles.darkResetWrapper : ''}`}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isSubmitting || isDeleting}
                                className={`${styles.clearButton} ${isDark ? styles.darkClearButton : ''}`}
                            >
                                {t.cancel}
                            </button>
                        </div>
                        {isEditing && typeof handleDelete === 'function' && (
                            <div className={`${styles.deleteWrapper} ${isDark ? styles.darkDeleteWrapper : ''}`}>
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    disabled={isSubmitting || isDeleting}
                                    className={`${styles.deleteButton} ${isDark ? styles.darkDeleteButton : ''}`}
                                >
                                    {isDeleting ? <CircularProgress size={20} color="inherit" /> : t.delete}
                                </button>
                            </div>
                        )}
                        <div className={`${styles.confirmationWrapper} ${isDark ? styles.darkConfirmationWrapper : ''}`}>
                            <button
                                type="submit"
                                className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`}
                                disabled={isSubmitting || isDeleting}
                            >
                                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t.confirm}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
